<template>
  <NPopper
    v-model="isOpen"
    placement="bottom-end"
    offset-y="4"
    :wrapper-class="wrapperClass"
    click-persist
  >
    <div class="relative">
      <div class="flex items-center transition duration-300 mb-1 rounded-md">
        <NInput
          ref="inputRef"
          :model-value="inputTerm"
          :placeholder="t('search.search_input_placeholder')"
          class="flex-grow !mb-0"
          size="small"
          @update:model-value="updateTerm"
          @focusin="handleSearchInputFocus"
        >
          <template #prefix>
            <NegarSearchLightIcon class="ml-1" />
          </template>

          <template v-if="showPostfix" #postfix>
            <SymbolActions
              :is-show-market-depth="isShowMarketDepth"
              @handle-market-depth="emit('handleMarketDepth')"
              @click.stop
            />
          </template>
        </NInput>
      </div>
    </div>

    <template #content>
      <NTransitionFade ref="popperContentRef">
        <div
          v-if="isShowSearchItems"
          class="p-2.5 bg-neutral-primary max-h-96 overflow-auto shadow-maxi rounded-md"
          v-bind="$attrs"
        >
          <RecentSearch
            v-if="
              recentSearchSymbols.length && !inputTerm && isShowRecentSearch
            "
            :recent-search-symbols="recentSearchSymbols"
            @handle-symbol-click="handleSearchSymbolsItemClick"
            @remove-recent-item="removeSymbolFromRecentSearch"
          />

          <ComponentState v-else :fetch-status="searchSymbolsStatus">
            <template #loading>
              <SearchResultSkeleton />
            </template>

            <template #error>
              <Error @retry="searchSymbols" />
            </template>

            <SymbolSearchResult
              v-if="inputTerm && symbols.length"
              :grouped-symbols="groupedSymbols"
              :term="inputTerm"
              :relevant-symbol="relevantSymbol"
              @handle-symbol-click="handleSearchSymbolsItemClick"
            />

            <SymbolSearchEmpty
              v-else-if="inputTerm && !symbols.length"
              :term="inputTerm"
            />
          </ComponentState>
        </div>
      </NTransitionFade>
    </template>
  </NPopper>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { watchDebounced, onClickOutside } from '@vueuse/core';
import { NegarSearchLightIcon } from '@negar/icons';
import { translate } from '@libs/utils';
import { useSearchSymbolQuery } from '@core/symbol';

import {
  SymbolActions,
  SymbolSearchResult,
  SymbolSearchEmpty,
} from '@/modules/symbol';

import Error from '@/components/Error.vue';
import ComponentState from '@/components/ComponentState.vue';

import RecentSearch from './RecentSearch.vue';
import SearchResultSkeleton from './SearchResultSkeleton.vue';

type SymbolSearchEmits = {
  (e: 'handleFocus'): void;
  (e: 'handleMarketDepth'): void;
  (
    e: 'updateSymbol',
    symbol: { isin: string | null; name: string | null }
  ): void;
};
type SymbolSearchProps = {
  showPostfix?: boolean;
  wrapperClass?: string;
  isShowMarketDepth?: boolean;
  isShowRecentSearch?: boolean;
  activeSymbolName?: string;
};

const props = withDefaults(defineProps<SymbolSearchProps>(), {
  showPostfix: false,
  wrapperClass: '',
  isShowRecentSearch: false,
  activeSymbolName: '',
});

const emit = defineEmits<SymbolSearchEmits>();

const { t } = useI18n();

const inputRef = ref();
const isOpen = ref(false);
const shouldSearchSymbol = ref(true);
const inputTerm = ref(props.activeSymbolName);
const popperContentRef = ref();

const {
  symbols,
  groupedSymbols,
  searchSymbols,
  recentSearchSymbols,
  addToRecentSearchSymbols,
  removeSymbolFromRecentSearch,
  searchSymbolsStatus,
} = useSearchSymbolQuery(inputTerm);

watchDebounced(
  inputTerm,
  (value) => {
    if (value && shouldSearchSymbol.value) {
      searchSymbols();
    }
  },
  { debounce: 50 }
);

onClickOutside(
  popperContentRef,
  () => {
    isOpen.value = false;
  },
  { detectIframe: true }
);

const relevantSymbol = computed(() => {
  return symbols.value.find((symbol) => symbol.name === inputTerm.value);
});

const isShowSearchItems = computed(
  () =>
    inputTerm.value ||
    (recentSearchSymbols.value.length &&
      !inputTerm.value &&
      props.isShowRecentSearch)
);

const updateTerm = (value: string) => {
  inputTerm.value = translate(value);
};

const handleSearchInputFocus = (event) => {
  shouldSearchSymbol.value = true;
  event.target.select();
  inputTerm.value = '';
};

const handleSearchSymbolsItemClick = (symbol) => {
  isOpen.value = false;

  if (props.isShowRecentSearch) {
    addToRecentSearchSymbols(symbol);
  }

  emit('updateSymbol', symbol);
};

watch(
  () => [props.activeSymbolName, isOpen.value],
  () => {
    if (!isOpen.value) {
      shouldSearchSymbol.value = false;

      inputTerm.value = props.activeSymbolName;
    }
  },
  { immediate: true }
);
</script>
